/**
 * SoleadLab 2.0 - Data Point Placement Hook
 *
 * Enables click-to-place mode for adding new data points on the map.
 * When placement mode is active:
 * - Cursor changes to crosshair
 * - Click captures [lng, lat] and opens DataPointConfigModal
 * - Escape cancels placement mode
 *
 * Port of enterDataPointPlaceMode() / __handleDataPointPlaceMode()
 * from editor (23).html (lines 17934–17980).
 */

import { useEffect, useCallback } from 'react';
import type { Map as MapboxMap, MapMouseEvent } from 'mapbox-gl';

// ============================================
// Types
// ============================================

interface PlacementCallbacks {
  onPlace: (coordinates: [number, number]) => void;
  onCancel: () => void;
}

// ============================================
// Hook
// ============================================

/**
 * Manages data point placement mode on the map.
 *
 * @param mapRef - Ref to the Mapbox map instance
 * @param isMapReady - Whether the map is fully loaded
 * @param isPlacing - Whether placement mode is active
 * @param callbacks - onPlace and onCancel callbacks
 */
export default function useDataPointPlacement(
  mapRef: React.RefObject<MapboxMap | null>,
  isMapReady: boolean,
  isPlacing: boolean,
  callbacks: PlacementCallbacks
) {
  const { onPlace, onCancel } = callbacks;

  // Handle map click during placement mode
  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      if (!isPlacing) return;
      const coordinates: [number, number] = [e.lngLat.lng, e.lngLat.lat];
      onPlace(coordinates);
    },
    [isPlacing, onPlace]
  );

  // Handle Escape key to cancel placement
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isPlacing) {
        onCancel();
      }
    },
    [isPlacing, onCancel]
  );

  // Set up/remove cursor and event listeners
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    if (isPlacing) {
      map.getCanvas().style.cursor = 'crosshair';
      map.on('click', handleMapClick);
      window.addEventListener('keydown', handleKeyDown);

      return () => {
        map.getCanvas().style.cursor = '';
        map.off('click', handleMapClick);
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [isPlacing, isMapReady, handleMapClick, handleKeyDown]);
}
